import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import { MyLocation, AddCircleOutline, Code, CheckCircle, CameraAlt, PhotoLibrary, DeleteOutline } from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { cloudKitService } from '../services/cloudkit';
import { getDeveloperLocation } from './DeveloperSettings';

interface LocationState {
  latitude: number;
  longitude: number;
  error: string | null;
  loading: boolean;
  isDeveloperLocation: boolean;
}

export function CreateShare() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [itemName, setItemName] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemSize, setItemSize] = useState('');
  const [itemQuantity, setItemQuantity] = useState<number>(1);
  const [itemSharedBy, setItemSharedBy] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [location, setLocation] = useState<LocationState>({
    latitude: 0,
    longitude: 0,
    error: null,
    loading: true,
    isDeveloperLocation: false,
  });

  useEffect(() => {
    const initAuth = async () => {
      try {
        const authenticated = await cloudKitService.setupAuth();
        setIsAuthenticated(authenticated);
        if (authenticated) {
          const userName = cloudKitService.getUserName();
          if (userName) {
            setItemSharedBy(userName);
          }
        }
      } catch (error) {
        console.error('Auth setup failed:', error);
      } finally {
        setAuthLoading(false);
      }
    };

    const handleAuthChange = (event: Event) => {
      const customEvent = event as CustomEvent<{ authenticated: boolean; userName: string }>;
      setIsAuthenticated(customEvent.detail.authenticated);
      if (customEvent.detail.userName) {
        setItemSharedBy(customEvent.detail.userName);
      }
    };

    window.addEventListener('cloudkit-auth-change', handleAuthChange);

    const timer = setTimeout(initAuth, 200);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('cloudkit-auth-change', handleAuthChange);
    };
  }, []);

  useEffect(() => {
    const devLocation = getDeveloperLocation();
    if (devLocation) {
      setLocation({
        latitude: devLocation.latitude,
        longitude: devLocation.longitude,
        error: null,
        loading: false,
        isDeveloperLocation: true,
      });
      return;
    }

    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            error: null,
            loading: false,
            isDeveloperLocation: false,
          });
        },
        (error) => {
          setLocation({
            latitude: 0,
            longitude: 0,
            error: error.message,
            loading: false,
            isDeveloperLocation: false,
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        }
      );
    } else {
      setLocation({
        latitude: 0,
        longitude: 0,
        error: 'Geolocation is not supported by this browser',
        loading: false,
        isDeveloperLocation: false,
      });
    }
  }, []);

  const resizeImage = (file: File, maxDimension: number = 1024): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let canvasWidth = img.width;
          let canvasHeight = img.height;

          if (canvasWidth > maxDimension || canvasHeight > maxDimension) {
            if (canvasWidth > canvasHeight) {
              canvasHeight = (canvasHeight / canvasWidth) * maxDimension;
              canvasWidth = maxDimension;
            } else {
              canvasWidth = (canvasWidth / canvasHeight) * maxDimension;
              canvasHeight = maxDimension;
            }
          }

          canvas.width = canvasWidth;
          canvas.height = canvasHeight;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
          const base64DataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(base64DataUrl);
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = event.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoCapture = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const resizedBase64DataUrl = await resizeImage(file);
      setPhotoPreviewUrl(resizedBase64DataUrl);
    } catch (error) {
      console.error('Failed to process photo:', error);
      setSubmitError('Failed to process photo');
    }
  };

  const removePhoto = () => {
    setPhotoPreviewUrl(null);
    if (cameraInputRef.current) cameraInputRef.current.value = '';
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!itemName.trim()) {
      setSubmitError('Item name is required');
      return;
    }

    if (location.latitude === 0 && location.longitude === 0) {
      setSubmitError('Location is required. Please enable location services.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Strip data URI prefix to get raw base64 for CloudKit
      const photoBase64 = photoPreviewUrl
        ? photoPreviewUrl.replace(/^data:image\/\w+;base64,/, '')
        : undefined;

      await cloudKitService.createShare({
        itemName: itemName.trim(),
        itemDescription: itemDescription.trim(),
        itemSize: itemSize.trim(),
        itemQuantity,
        itemSharedBy: itemSharedBy.trim(),
        latitude: location.latitude,
        longitude: location.longitude,
        itemPhoto: photoBase64,
      });

      setSubmitSuccess(true);
      queryClient.invalidateQueries({ queryKey: ['shares'] });

      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to create share');
    } finally {
      setIsSubmitting(false);
    }
  };

  const refreshLocation = () => {
    const devLocation = getDeveloperLocation();
    if (devLocation) {
      setLocation({
        latitude: devLocation.latitude,
        longitude: devLocation.longitude,
        error: null,
        loading: false,
        isDeveloperLocation: true,
      });
      return;
    }

    setLocation((prev) => ({ ...prev, loading: true, error: null }));
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          error: null,
          loading: false,
          isDeveloperLocation: false,
        });
      },
      (error) => {
        setLocation((prev) => ({
          ...prev,
          error: error.message,
          loading: false,
          isDeveloperLocation: false,
        }));
      }
    );
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="flex-start"
      minHeight="calc(100vh - 80px)"
      p={{ xs: 2, md: 4 }}
    >
      <Card sx={{ maxWidth: 520, width: '100%', border: '1px solid rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ p: { xs: 3, md: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
              }}
            >
              <AddCircleOutline sx={{ fontSize: 28, color: '#ffffff' }} />
            </Box>
            <Typography variant="h5">Share an Item</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Share something with your community
            </Typography>
          </Box>

          {/* Authentication Status */}
          {authLoading ? (
            <Box display="flex" alignItems="center" gap={1} mb={2} justifyContent="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Setting up authentication...</Typography>
            </Box>
          ) : isAuthenticated ? (
            <Alert
              severity="success"
              icon={<CheckCircle fontSize="small" />}
              sx={{ mb: 2, borderRadius: 2 }}
            >
              Signed in with Apple ID
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Sign in with your Apple ID to create shares
            </Alert>
          )}

          {/* CloudKit Sign In/Out Buttons */}
          <Box
            id="apple-sign-in-button"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              minHeight: 44,
              mb: 2,
            }}
          />
          <Box
            id="apple-sign-out-button"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 2,
            }}
          />

          {submitSuccess && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              Share created successfully! Redirecting...
            </Alert>
          )}

          {submitError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {submitError}
            </Alert>
          )}

          {/* Location */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
              Location
            </Typography>
            {location.loading ? (
              <Box display="flex" alignItems="center" gap={1}>
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Getting location...</Typography>
              </Box>
            ) : location.error ? (
              <Alert severity="warning" sx={{ py: 0.5, borderRadius: 2 }}>
                {location.error}
                <Button size="small" onClick={refreshLocation} sx={{ ml: 1 }}>
                  Retry
                </Button>
              </Alert>
            ) : (
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Chip
                  icon={location.isDeveloperLocation ? <Code /> : <MyLocation />}
                  label={`${location.latitude.toFixed(4)}, ${location.longitude.toFixed(4)}`}
                  size="small"
                  color={location.isDeveloperLocation ? 'warning' : 'success'}
                />
                {location.isDeveloperLocation && (
                  <Chip
                    label="Developer Override"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
                <Button size="small" onClick={refreshLocation}>
                  Refresh
                </Button>
              </Box>
            )}
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Photo Capture */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
            }}
          >
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
              Photo
            </Typography>
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={handlePhotoCapture}
              style={{ display: 'none' }}
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoCapture}
              style={{ display: 'none' }}
            />
            {photoPreviewUrl ? (
              <Box sx={{ position: 'relative', display: 'inline-block' }}>
                <Box
                  component="img"
                  src={photoPreviewUrl}
                  alt="Captured photo"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 240,
                    borderRadius: 2,
                    border: '1px solid #e2e8f0',
                    display: 'block',
                  }}
                />
                <IconButton
                  onClick={removePhoto}
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: '#fff',
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                  }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CameraAlt />}
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={isSubmitting}
                >
                  Take Photo
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PhotoLibrary />}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                >
                  Choose Photo
                </Button>
              </Box>
            )}
          </Box>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Item Name"
              variant="outlined"
              value={itemName}
              onChange={(e) => setItemName(e.target.value)}
              margin="normal"
              required
              disabled={isSubmitting}
            />
            <TextField
              fullWidth
              label="Description"
              variant="outlined"
              value={itemDescription}
              onChange={(e) => setItemDescription(e.target.value)}
              margin="normal"
              placeholder="Describe the item..."
              multiline
              rows={2}
              disabled={isSubmitting}
            />
            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <TextField
                fullWidth
                label="Size"
                variant="outlined"
                value={itemSize}
                onChange={(e) => setItemSize(e.target.value)}
                margin="normal"
                placeholder="e.g., Small, Medium, Large"
                disabled={isSubmitting}
              />
              <TextField
                fullWidth
                label="Quantity"
                variant="outlined"
                type="number"
                value={itemQuantity}
                onChange={(e) => setItemQuantity(parseInt(e.target.value) || 0)}
                margin="normal"
                inputProps={{ min: 0 }}
                disabled={isSubmitting}
                sx={{ maxWidth: { xs: '100%', sm: 140 } }}
              />
            </Box>
            <TextField
              fullWidth
              label="Shared By"
              variant="outlined"
              value={itemSharedBy}
              onChange={(e) => setItemSharedBy(e.target.value)}
              margin="normal"
              placeholder="Your name"
              disabled={isSubmitting}
            />
            <Button
              fullWidth
              variant="contained"
              type="submit"
              disabled={isSubmitting || location.loading || submitSuccess}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <AddCircleOutline />}
              sx={{
                mt: 3,
                py: 1.5,
                fontSize: '1rem',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
                },
              }}
            >
              {isSubmitting ? 'Creating...' : 'Share Item'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
